/**
 * Member Table Component
 *
 * Displays organization members with expandable rows and inline role management.
 */

import { useCallback, useMemo,useState } from 'react';
import { ExpandableDataTable } from '@features/admin-v2/components/expandable-data-table';
import {
  Add,
  Cancel,
  Close,
  Email,
  ExpandLess,
  ExpandMore,
  Person,
  Phone,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import type { ProviderRoleInfo } from '@store/generated/organizations';
import {
  getRoleBadgeColor,
  getRoleDisplayName,
  ROLES,
} from '@utils/roleUtils';
import { useSnackbar } from 'notistack';

import {
  useReactivateMemberMutation,
  useRemoveMemberMutation,
  useUpdateMemberRolesMutation,
} from '../store/api';
import { useOperationLoading } from '../store/hooks';
import type { OrganizationMemberTableRow } from '../types';

interface MemberTableProps {
  title: string;
  members: OrganizationMemberTableRow[];
  expandedRows: Set<string>;
  onToggleExpand: (userId: string) => void;
  onMemberChange: () => void;
  availableRoles?: ProviderRoleInfo[];
  isInactive?: boolean;
}

export function MemberTable({
  title,
  members,
  expandedRows,
  onToggleExpand,
  onMemberChange,
  availableRoles = [],
  isInactive = false,
}: MemberTableProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { loadingOperations, setOperationLoading, isOperationLoading } = useOperationLoading();

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    userId?: string;
    role?: string;
    action?: 'remove' | 'deactivate';
  }>({ open: false });

  const [roleMenuAnchor, setRoleMenuAnchor] = useState<HTMLElement | null>(null);
  const [roleMenuUserId, setRoleMenuUserId] = useState<string | null>(null);

  // Mutations
  const [removeMember] = useRemoveMemberMutation();
  const [reactivateMember] = useReactivateMemberMutation();
  const [updateRoles] = useUpdateMemberRolesMutation();

  // Handle role removal
  const handleRemoveRole = useCallback(async () => {
    if (!confirmDialog.userId) return;

    const operationKey = `remove-${confirmDialog.userId}-${confirmDialog.role || 'all'}`;
    setOperationLoading(operationKey, true);

    try {
      // For removing a specific role, we need to update the roles array
      if (confirmDialog.role) {
        const member = members.find(m => m.userId === confirmDialog.userId);
        if (member) {
          const newRoles = member.roles.filter(r => r !== confirmDialog.role);

          if (newRoles.length === 0) {
            // If no roles left, deactivate the member
            await removeMember({ userId: confirmDialog.userId }).unwrap();
            enqueueSnackbar('Member deactivated successfully', { variant: 'success' });
          } else {
            await updateRoles({
              userId: confirmDialog.userId,
              providerUpdateRolesRequest: { roles: newRoles },
            }).unwrap();
            enqueueSnackbar('Role removed successfully', { variant: 'success' });
          }
        }
      } else {
        // Deactivate the member entirely
        await removeMember({ userId: confirmDialog.userId }).unwrap();
        enqueueSnackbar('Member deactivated successfully', { variant: 'success' });
      }

      onMemberChange();
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || 'Failed to remove role', { variant: 'error' });
    } finally {
      setOperationLoading(operationKey, false);
      setConfirmDialog({ open: false });
    }
  }, [confirmDialog, members, removeMember, updateRoles, enqueueSnackbar, onMemberChange, setOperationLoading]);

  // Handle role addition
  const handleAddRole = useCallback(async (userId: string, role: string) => {
    const operationKey = `add-${userId}-${role}`;
    setOperationLoading(operationKey, true);

    try {
      const member = members.find(m => m.userId === userId);
      if (member) {
        const newRoles = [...member.roles, role];
        await updateRoles({
          userId,
          providerUpdateRolesRequest: { roles: newRoles },
        }).unwrap();
        enqueueSnackbar('Role added successfully', { variant: 'success' });
        onMemberChange();
      }
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || 'Failed to add role', { variant: 'error' });
    } finally {
      setOperationLoading(operationKey, false);
      setRoleMenuAnchor(null);
      setRoleMenuUserId(null);
    }
  }, [members, updateRoles, enqueueSnackbar, onMemberChange, setOperationLoading]);

  // Handle reactivation
  const handleReactivateUser = useCallback(async (userId: string) => {
    const operationKey = `reactivate-${userId}`;
    setOperationLoading(operationKey, true);

    try {
      await reactivateMember({ userId }).unwrap();
      enqueueSnackbar('Member reactivated successfully', { variant: 'success' });
      onMemberChange();
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || 'Failed to reactivate member', { variant: 'error' });
    } finally {
      setOperationLoading(operationKey, false);
    }
  }, [reactivateMember, enqueueSnackbar, onMemberChange, setOperationLoading]);

  // Render expanded content
  const renderExpandedContent = useCallback((member: OrganizationMemberTableRow) => (
    <Box>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
            <Person className="h-4 w-4 mr-1" />
            User Information
          </h4>
          <dl className="space-y-1 text-sm">
            <div className="flex items-center">
              <dt className="text-gray-500 w-24">Name:</dt>
              <dd className="text-gray-900">{member.name || 'Not provided'}</dd>
            </div>
            <div className="flex items-center">
              <dt className="text-gray-500 w-24 flex items-center">
                <Email className="h-3 w-3 mr-1" />
                Email:
              </dt>
              <dd className="text-gray-900">{member.email || 'Not provided'}</dd>
            </div>
            <div className="flex items-center">
              <dt className="text-gray-500 w-24 flex items-center">
                <Phone className="h-3 w-3 mr-1" />
                Phone:
              </dt>
              <dd className="text-gray-900">{member.phoneNumber || 'Not provided'}</dd>
            </div>
          </dl>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Metrics & Details</h4>
          <dl className="space-y-1 text-sm">
            <div className="flex items-center">
              <dt className="text-gray-500 w-32">User ID:</dt>
              <dd className="text-gray-900 font-mono text-xs">{member.userId}</dd>
            </div>
            <div className="flex items-center">
              <dt className="text-gray-500 w-32">Notes Created:</dt>
              <dd className="text-gray-900">{member.notesCreated || 0}</dd>
            </div>
            <div className="flex items-center">
              <dt className="text-gray-500 w-32">Status:</dt>
              <dd className="text-gray-900">
                {member.status === 'active' ? (
                  <Chip label="Active" color="success" size="small" />
                ) : (
                  <Chip label="Inactive" color="default" size="small" />
                )}
              </dd>
            </div>
            {member.deactivatedAt && (
              <div className="flex items-center">
                <dt className="text-gray-500 w-32">Deactivated:</dt>
                <dd className="text-gray-900">
                  {new Date(member.deactivatedAt).toLocaleDateString()}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Action buttons */}
      {!isInactive && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex gap-2 justify-end">
            <Button
              variant="outlined"
              color="error"
              size="small"
              disabled={isOperationLoading(`deactivate-${member.userId}`)}
              onClick={() => {
                setConfirmDialog({
                  open: true,
                  userId: member.userId,
                  action: 'deactivate',
                });
              }}
              startIcon={isOperationLoading(`deactivate-${member.userId}`) ? <CircularProgress size={16} /> : <Cancel />}
            >
              {isOperationLoading(`deactivate-${member.userId}`) ? 'Deactivating...' : 'Deactivate User'}
            </Button>
          </div>
        </div>
      )}
    </Box>
  ), [isInactive, isOperationLoading]);

  // Column definitions
  const columns = useMemo(() => {
    if (isInactive) {
      // Inactive members columns
      return [
        {
          key: 'expand' as any,
          label: '',
          render: (_: unknown, member: OrganizationMemberTableRow) => (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(member.userId);
              }}
            >
              {expandedRows.has(member.userId) ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          ),
        },
        {
          key: 'name' as any,
          label: 'Name',
          render: (value: unknown) => (
            <div className="flex items-center">
              <Person className="h-5 w-5 text-gray-400 mr-2" />
              <span className="font-medium text-gray-500">{String(value || 'Unknown')}</span>
            </div>
          ),
        },
        {
          key: 'roles' as any,
          label: 'Previous Roles',
          render: (_: unknown, member: OrganizationMemberTableRow) => (
            <div className="flex gap-1 flex-wrap items-center">
              {member.roles.map((role) => (
                <Chip
                  key={role}
                  label={getRoleDisplayName(role)}
                  color="default"
                  size="small"
                  sx={{ opacity: 0.6 }}
                />
              ))}
              {member.roles.length === 0 && (
                <span className="text-gray-400 text-sm">No previous roles</span>
              )}
            </div>
          ),
        },
        {
          key: 'email' as any,
          label: 'Email',
          render: (value: unknown) => (
            <span className="text-gray-500">{String(value || 'No email')}</span>
          ),
        },
        {
          key: 'userId' as any,
          label: 'Actions',
          render: (_: unknown, member: OrganizationMemberTableRow) => {
            const isLoading = isOperationLoading(`reactivate-${member.userId}`);
            return (
              <Button
                size="small"
                variant="contained"
                color="success"
                disabled={isLoading}
                onClick={(e) => {
                  e.stopPropagation();
                  handleReactivateUser(member.userId);
                }}
                startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {isLoading ? 'Reactivating...' : 'Reactivate'}
              </Button>
            );
          },
        },
      ];
    }

    // Active members columns
    return [
      {
        key: 'expand' as any,
        label: '',
        render: (_: unknown, member: OrganizationMemberTableRow) => (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(member.userId);
            }}
          >
            {expandedRows.has(member.userId) ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        ),
      },
      {
        key: 'name' as any,
        label: 'Name',
        render: (value: unknown) => (
          <div className="flex items-center">
            <Person className="h-5 w-5 text-gray-400 mr-2" />
            <span className="font-medium text-gray-900">{String(value || 'Unknown')}</span>
          </div>
        ),
      },
      {
        key: 'roles' as any,
        label: 'Roles',
        render: (_: unknown, member: OrganizationMemberTableRow) => {
          const allPossibleRoles = [
            ROLES.CLINICIAN,
            ROLES.OPERATIONS_SUPPORT,
            ROLES.PRACTICE_ADMINISTRATOR,
            ROLES.SUPERVISOR,
          ];
          const canAddRole = allPossibleRoles.some(role => !member.roles.includes(role));

          return (
            <div className="flex gap-1 flex-wrap items-center">
              {member.roles.map((role) => {
                const isOnlyRole = member.roles.length === 1;
                const operationKey = `remove-${member.userId}-${isOnlyRole ? 'all' : role}`;
                const isLoading = isOperationLoading(operationKey);

                return (
                  <Chip
                    key={role}
                    label={getRoleDisplayName(role)}
                    color={getRoleBadgeColor(role)}
                    size="small"
                    disabled={isLoading}
                    onDelete={!isLoading ? () => {
                      setConfirmDialog({
                        open: true,
                        userId: member.userId,
                        role: isOnlyRole ? undefined : role,
                        action: 'remove',
                      });
                    } : undefined}
                    deleteIcon={
                      isLoading ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <Close style={{ fontSize: 16 }} />
                      )
                    }
                  />
                );
              })}

              {canAddRole && (
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRoleMenuAnchor(e.currentTarget);
                    setRoleMenuUserId(member.userId);
                  }}
                  title="Add role"
                >
                  <Add style={{ fontSize: 16 }} />
                </IconButton>
              )}
            </div>
          );
        },
      },
      {
        key: 'email' as any,
        label: 'Email',
        render: (value: unknown) => (
          <span className="text-gray-600">{String(value || 'No email')}</span>
        ),
      },
    ];
  }, [isInactive, expandedRows, onToggleExpand, isOperationLoading, handleReactivateUser]);

  return (
    <>
      <ExpandableDataTable
        title={title}
        columns={columns}
        data={members}
        total={members.length}
        loading={false}
        emptyMessage={isInactive ? 'No inactive members' : 'No active members'}
        expandedRows={expandedRows}
        getRowId={(member: OrganizationMemberTableRow) => member.userId}
        renderExpandedContent={renderExpandedContent as any}
      />

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false })}>
        <DialogTitle>
          {confirmDialog.action === 'deactivate'
            ? 'Deactivate User'
            : confirmDialog.role
            ? 'Remove Role'
            : 'Remove User'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDialog.action === 'deactivate'
              ? 'Are you sure you want to deactivate this user? They will be moved to the inactive members list.'
              : confirmDialog.role
              ? `Are you sure you want to remove the "${getRoleDisplayName(confirmDialog.role)}" role from this user?`
              : 'Are you sure you want to remove this user from the organization?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false })}>Cancel</Button>
          <Button onClick={handleRemoveRole} color="error" variant="contained">
            {confirmDialog.action === 'deactivate' ? 'Deactivate' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Role Menu */}
      <Menu
        anchorEl={roleMenuAnchor}
        open={Boolean(roleMenuAnchor && roleMenuUserId)}
        onClose={() => {
          setRoleMenuAnchor(null);
          setRoleMenuUserId(null);
        }}
      >
        <MenuItem disabled>
          <Typography variant="caption" color="text.secondary">
            Select a role to add:
          </Typography>
        </MenuItem>
        <Divider />
        {roleMenuUserId && (() => {
          const member = members.find(m => m.userId === roleMenuUserId);
          if (!member) return null;

          const allRoles = [
            ROLES.CLINICIAN,
            ROLES.OPERATIONS_SUPPORT,
            ROLES.PRACTICE_ADMINISTRATOR,
            ROLES.SUPERVISOR,
          ];

          const availableRolesToAdd = allRoles.filter(role => !member.roles.includes(role));

          return availableRolesToAdd.map(role => {
            const operationKey = `add-${roleMenuUserId}-${role}`;
            const isLoading = isOperationLoading(operationKey);

            return (
              <MenuItem
                key={role}
                disabled={isLoading}
                onClick={() => handleAddRole(roleMenuUserId, role)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Chip
                        label={getRoleDisplayName(role)}
                        size="small"
                        color={getRoleBadgeColor(role)}
                        sx={{ minWidth: 140 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        {role === ROLES.CLINICIAN && 'Healthcare provider who creates notes'}
                        {role === ROLES.OPERATIONS_SUPPORT && 'Administrative support staff'}
                        {role === ROLES.PRACTICE_ADMINISTRATOR && 'Manages org and billing'}
                        {role === ROLES.SUPERVISOR && 'Clinical supervisor'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </MenuItem>
            );
          });
        })()}
      </Menu>
    </>
  );
}